import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IEvents, EventsFormValues } from "../../../domain/models/Events";
import BaseModal from "../../modals/BaseModal";
import { ImageUploader } from "../../shared/ImageUploader";
import { Langs } from "../../../domain/enums/common/LangsEnum";
import { Tabs, Tab } from "@mui/material";
import { DEFAULT_LANGUAGE } from "../../../Config";
import { Owner } from "../../../domain/enums/owner-enum";
import { Purpose } from "../../../domain/enums/purpose-enum";
import { useFileEncoder } from "../../../custom-hooks/useFileEncoder";
import { EventsFields, eventsSchemaFactory } from "../../../validation-schemas/EventsSchemas";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { InputType } from "../../../types/InputType";
import TranslationFieldsRenderer from "../../shared/form-inputs/TranslationFieldsRenderer";
import { IEventsTranslation } from "../../../domain/models/EventsTranslation";
import { Location } from "../../../domain/enums/location-enum";
import { Format } from "../../../domain/enums/format-enum";

interface EventsEditorProps {
    entity?: IEvents | null;
    onClose: () => void;
    onSave: (saved: IEvents) => void;
    translateFields: (
        translationFields: Record<string, string>,
        sourceLang: Langs,
        targetLang: Langs
    ) => Promise<Record<string, string>>;
}

const EventsEditor: FC<EventsEditorProps> = ({ entity, onClose, onSave, translateFields }) => {
    const { t } = useTranslation();
    const { encodeFiles } = useFileEncoder();
    const [selectedLang, setSelectedLang] = useState<Langs>(DEFAULT_LANGUAGE);
    const [existingImageUrl, setExistingImageUrl] = useState(entity?.pictureExists() ? entity?.getPictureUrl() : null);
    const tabs = useMemo(() => Object.values(Langs), []);

    const translationsInitial = useMemo(() => {
        return Object.values(Langs).map((lang) => {
            const existing = entity?.translations?.find((t) => t?.languageCode === lang);
            return {
                languageCode: lang,
                eventId: entity?.id,
                title: existing?.title ?? "",
                subtitle: existing?.subtitle ?? "",
                description: existing?.description ?? "",
            };
        }) as EventsFormValues["translations"];
    }, [entity?.translations]);

    const { form } = useCustomFormik<EventsFormValues>({
        initial: {
            files: entity?.files ?? [],
            translations: translationsInitial,
            eventDate: entity?.eventDate ? new Date(entity.eventDate).toISOString().slice(0, 16) : "",
            location: entity?.location ?? "",
            format: entity?.format ?? "",
        },
        fields: EventsFields,
        schema: eventsSchemaFactory(t),
        onSubmit: (values) => {
            const validLangs = values.translations.filter(
                (l: IEventsTranslation) => l.title?.trim()?.length && l.subtitle?.trim()?.length && l.description?.trim()?.length
            );

            const updated: IEvents = {
                ...entity,
                ...values,
                eventDate: values.eventDate ? new Date(values.eventDate) : entity?.eventDate || new Date(),
                location: values.location as Location,
                format: values.format as Format,
                translations: validLangs,
            };
            onSave(updated);
            onClose();
        },
    });

    const isDefaultLangValid = useMemo(() => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        return !!(
            defaultTr &&
            defaultTr.title?.trim().length > 1 &&
            defaultTr.subtitle?.trim().length > 1 &&
            defaultTr.description?.trim().length > 1
        );
    }, [form.values.translations]);

    const handleFileChange = useCallback(
        async (file: File | null) => {
            if (!file) {
                form.setFieldValue("files", []);
                return;
            }
            const dataUrl = await encodeFiles(file);
            const newFiles = [
                {
                    order: 1,
                    ownerType: Owner.EVENTS,
                    purposeType: Purpose.COVER,
                    fileType: file.type.split("/")[1],
                    dataUrl,
                },
            ];
            form.setFieldValue("files", newFiles);
        },
        [encodeFiles]
    );

    const handleTranslateEntity = async () => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        if (!defaultTr) return;

        const payload = {
            title: defaultTr.title ?? "",
            subtitle: defaultTr.subtitle ?? "",
            description: defaultTr.description ?? "",
        };

        try {
            const translated = await translateFields(payload, DEFAULT_LANGUAGE, selectedLang);
            // ensure translated has the expected keys
            const translatedTr = {
                languageCode: selectedLang,
                eventId: entity?.id,
                title: translated.title ?? "",
                subtitle: translated.subtitle ?? "",
                description: translated.description ?? "",
            } as EventsFormValues["translations"][number];

            const idx = form.values.translations.findIndex((t) => t.languageCode === selectedLang);
            if (idx >= 0) {
                form.setFieldValue(`translations[${idx}]`, translatedTr);
            } else {
                form.setFieldValue("translations", [...form.values.translations, translatedTr]);
            }
        } catch (err) {
            console.error("translate error", err);
        }
    };

    return (
        <BaseModal
            isVisible={true}
            setIsVisible={onClose}
            title={entity ? t("console.events.editEvents") : t("console.events.createEvents")}
            confirmTitle={t("console.common.save")}
            cancelTitle={t("console.common.cancel")}
            confirmHandler={form.handleSubmit}
            closeHandler={onClose}
            additionalWrapClassName="w-[90vw]"
            confirmButtonDisabled={!form.isValid}
        >
            <div className="flex gap-6 h-[70vh]">
                {/* or 0.4 */}
                <div className="flex flex-col gap-4 flex-[0.35]">
                    <div className="text-sm font-medium text-light-text-100 dark:text-dark-text-100">
                        {t("console.common.cover")}
                    </div>
                    <ImageUploader
                        imageUrl={existingImageUrl}
                        deleteImage={() => {
                            handleFileChange(null);
                            setExistingImageUrl(null);
                        }}
                        onChange={(file) => handleFileChange(file)}
                    />
                </div>

                <div className="flex flex-col flex-1 h-full overflow-auto">
                    <div className="flex flex-row justify-between">
                        <Tabs
                            value={selectedLang}
                            onChange={(_, newValue) => setSelectedLang(newValue as Langs)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map((lang) => (
                                <Tab
                                    key={lang}
                                    value={lang}
                                    label={lang.toUpperCase()}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: "medium",
                                    }}
                                    className="dark:text-dark-text-100"
                                />
                            ))}
                        </Tabs>

                        {selectedLang !== DEFAULT_LANGUAGE && (
                            <div className="flex items-center">
                                <button
                                    className={`w-48 text-white font-medium rounded-lg text-sm px-5 py-2 text-center ${isDefaultLangValid
                                        ? "bg-blue-700 hover:bg-blue-800"
                                        : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                    onClick={handleTranslateEntity}
                                    disabled={!isDefaultLangValid}
                                    title={
                                        (!isDefaultLangValid &&
                                            `${t(
                                                "console.common.fillDefaultLangFirst"
                                            )} ${DEFAULT_LANGUAGE.toUpperCase()}`) ||
                                        ""
                                    }
                                >
                                    {t("console.common.translateFromDefault")}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="overflow-auto mt-4">
                        <div className="mb-4 space-y-4">
                            {Object.entries(EventsFields)
                                .filter(([key]) => key !== "files" && key !== "translations")
                                .map(([key, cfg]) => {
                                    const fieldName = key;
                                    const value = form.values[key as keyof EventsFormValues];
                                    const errorKey = form.errors[key as keyof EventsFormValues] as string;

                                    if (key === "eventDate") {
                                        return (
                                            <div key={key} className="flex flex-col">
                                                <label className="block mb-2 text-lg font-semibold text-light-text-200 dark:text-dark-text-400">
                                                    {t(cfg.placeholderKey)}
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    name={fieldName}
                                                    value={value as string}
                                                    onChange={form.handleChange(fieldName)}
                                                    className="px-3 py-2 text-base text-light-text-200 dark:text-dark-bg-100 bg-white border-2 border-[#d0d5dd] shadow-sm rounded-lg"
                                                />
                                                <CustomError errorKey={errorKey} name={fieldName} />
                                            </div>
                                        );
                                    }

                                    if (cfg.type === "select") {
                                        return (
                                            <div key={key} className="flex flex-col">
                                                <label className="block mb-2 text-lg font-semibold text-light-text-200 dark:text-dark-text-400">
                                                    {t(cfg.placeholderKey)}
                                                </label>
                                                <select
                                                    name={fieldName}
                                                    value={value as string}
                                                    onChange={form.handleChange(fieldName)}
                                                    className="px-3 py-2 text-base text-light-text-200 dark:text-dark-bg-100 bg-white border-2 border-[#d0d5dd] shadow-sm rounded-lg"
                                                >
                                                    <option value="">{t(cfg.placeholderKey)}</option>
                                                    {cfg.selectOptions?.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {t(opt.labelKey)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <CustomError errorKey={errorKey} name={fieldName} />
                                            </div>
                                        );
                                    }

                                    return (
                                        <CustomInput
                                            key={key}
                                            type={cfg.type as InputType}
                                            labelKey={cfg.placeholderKey}
                                            name={fieldName}
                                            placeholderKey={cfg.placeholderKey}
                                            value={value as string}
                                            onChange={form.handleChange(fieldName)}
                                            errorKey={errorKey}
                                        />
                                    );
                                })}
                        </div>

                        <p className={`mt-1 ml-3 text-sm font-normal text-red-500`}>
                            {(typeof form.errors.translations === "string" && form.errors.translations) || "\u00A0"}
                        </p>

                        {form.values.translations.map((tr, index) => (
                            <div
                                className="overflow-auto"
                                key={tr.languageCode}
                                role="tabpanel"
                                hidden={selectedLang !== tr.languageCode}
                            >
                                <TranslationFieldsRenderer
                                    translation={tr}
                                    index={index}
                                    fieldConfigs={EventsFields.translations.objectArray}
                                    errors={(form.errors.translations?.[index] as any) || undefined}
                                    t={t}
                                    setFieldValue={(fieldName, value) => form.setFieldValue(fieldName, value)}
                                    handleFieldChange={(fieldName) => form.handleChange(fieldName)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};

export default EventsEditor;
